import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {
    GlobalMessageService,
    GlobalMessageType,
    ProductLoadingService,
    ProductService,
    StateWithProduct,
    Translatable,
    TranslatableParams,
    UserIdService,
} from '@spartacus/core';
import {Observable} from 'rxjs';
import {switchMap, take} from 'rxjs/operators';

import {AimoClassification, AimoFeature, AimoFeatureValue, AimoProduct} from '../../model/product.model';
import {AimoActiveCartService} from '../cart/aimo-active-cart.service';
import {AimoGtmProduct, AimoViewGtmProducts} from '../gtm/aimo-gtm.action';
import {AimoGTMProductAttributes, GTMEventCode} from '../gtm/aimo-gtm.model';

import {AimoProductConnector} from './aimo-product.connector';
import {AimoProductSearchConnector} from './search/aimo-product-search.connector';
import {AimoGtmStoredParams} from "../../model/cart.model";

export const CROSSSELING_PRODUCTS = 'CROSSELLING';
export const SIMILAR_PRODUCTS = 'SIMILAR';

@Injectable({
    providedIn: 'root',
})
export class AimoProductService extends ProductService {
    constructor(
        protected store: Store<StateWithProduct>,
        productLoading: ProductLoadingService,
        protected productSearchConnector: AimoProductSearchConnector,
        protected productConnector: AimoProductConnector,
        protected userIdService: UserIdService,
        protected globalMessage: GlobalMessageService,
    ) {
        super(store, productLoading);
    }

    // eslint-disable-next-line
    getAimoProduct(
        productCode: string,
        gtmProductAttributes?: AimoGTMProductAttributes,
    ): Observable<AimoProduct | undefined> {
        return this.productConnector.getAimoProduct(
            productCode,
            gtmProductAttributes?.category_code,
            gtmProductAttributes?.search_term,
        );
    }

    getProducts(products: string[], cartId?: string): Observable<AimoProduct[]> {
        return this.productSearchConnector.loadManyProducts(products, cartId);
    }

    getNutritionalContent(product: AimoProduct, code: string): AimoFeature {
        return this.getClassificationClass(product, 'nutritionalContent')?.features.find((nc) =>
            nc.code.endsWith('.' + code.toLowerCase()),
        );
    }

    getClassificationClass(product: AimoProduct, code: string): AimoClassification {
        return product?.classifications?.find((cl) => cl.code === code);
    }

    getClassification(product: AimoProduct, code: string): AimoFeature[] {
        return product?.classifications
            ?.map((c) => c.features)
            .reduce((x, y) => x.concat(y), [])
            .filter((f) => f.code.endsWith('.' + code.toLowerCase()));
    }

    getClassificationValueList(product: AimoProduct, code: string): string {
        return this.getClassification(product, code)
            ?.flatMap((value) => value.featureValues)
            .map((value) => value.value)
            .join(', ');
    }

    getClassificationNameList(product: AimoProduct, code: string): string {
        return this.getClassification(product, code)
            ?.flatMap((value) => value.featureValues)
            .map((value) => value.name)
            .join(', ');
    }

    getClassificationName(product: AimoProduct, code: string): string {
        return this.getClassification(product, code)
            ?.map((value) => value.name)
            .find((v) => v == v);
    }

    getClassificationValueName(product: AimoProduct, code: string): string {
        return this.getClassification(product, code)
            ?.map((value) => value.featureValues[0].name)
            .find((v) => v == v);
    }

    getClassificationValue(product: AimoProduct, code: string): AimoFeatureValue {
        return this.getClassification(product, code)
            ?.map((value) => value.featureValues[0])
            .find((v) => v == v);
    }

    getClassificationUnit(product: AimoProduct, code: string): string {
        return this.getClassification(product, code)
            ?.map((value) => value.featureUnit?.symbol)
            .find((v) => v == v);
    }

    excelExportProducts(query: string): void {
        const sub = this.userIdService
            .getUserId()
            .pipe(switchMap((userId) => this.productSearchConnector.excelExportProducts(query, {userId})))
            .subscribe((response) => {
                AimoActiveCartService.exportAsBinary(response);
                sub?.unsubscribe();
            });
    }

    safetyDataExport(code: string, fileName: string): void {
        const sub = this.productSearchConnector.safetyDataExport(code).subscribe((response) => {
            AimoActiveCartService.exportAsBinary({...response, name: fileName + '.pdf'});
            sub?.unsubscribe();
        });
    }

    fireProductListViewGtmEvent(
        products: AimoProduct[],
        totalHits?: number,
        searchTerm?: string,
        gtmParams?: AimoGtmStoredParams,
    ): void {
        if ((gtmParams === null || gtmParams === undefined) && products?.length > 0) {
            gtmParams = products[0]?.gtmProductAttributes?.gtmParams;
        }
        this.store.dispatch(
            new AimoViewGtmProducts(
                products,
                gtmParams?.gtmPromotionId ? GTMEventCode.ViewPromotion : GTMEventCode.ViewItemList,
                totalHits,
                searchTerm,
                undefined,
                undefined,
                gtmParams
            ),
        );
    }

    fireSingleProductGtmEvent(product: AimoProduct, event: GTMEventCode, fromAlternativeProducts?: boolean): void {
        this.store.dispatch(new AimoGtmProduct(product, event));
        if (fromAlternativeProducts) {
            this.store.dispatch(
                new AimoGtmProduct(product, GTMEventCode.ViewAlternativeProducts, this.getSimilarProducts(product)),
            );
        }
    }

    getSimilarProducts(product: AimoProduct): AimoProduct[] {
        return product.productReferences[SIMILAR_PRODUCTS]?.map((r) => r.target);
    }

    getCrossSellingProducts(product: AimoProduct): AimoProduct[] {
        return product.productReferences[CROSSSELING_PRODUCTS]?.map((r) => r.target);
    }

    toggleAvailabilityNotificationMessage(product: AimoProduct, done?: (value: void) => void) {
        return this.toggleAvailabilityNotification(product.code)
            .pipe(take(1))
            .subscribe(() => {
                this.globalMessage.add(
                    {
                        key: product.availabilityNotificationActive
                            ? 'aimo.product.removedAvailabilityNotificationSuccessfully'
                            : 'aimo.product.addedAvailabilityNotificationSuccessfully',
                        params: {
                            productCode: product.code,
                            productName: product.name,
                        } as TranslatableParams,
                    } as Translatable,
                    GlobalMessageType.MSG_TYPE_INFO,
                    6000,
                );
                product.availabilityNotificationActive = !product.availabilityNotificationActive;
                if (done) {
                    done();
                }
            });
    }

    toggleAvailabilityNotification(productCode: string): Observable<void> {
        return this.productConnector.toggleAvailabilityNotification(productCode);
    }

    getLastPurchasedTranslationKey(lastPurchased: Date): string {
        return lastPurchased.getTime() < new Date().getTime() ? 'aimo.product.lastPurchased' : 'aimo.product.lastPurchasedFuture';
    }
}
